import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, Paper, LinearProgress, Button, IconButton, Alert, TextField, MenuItem } from '@mui/material';
import { io, Socket } from 'socket.io-client';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import MicIcon from '@mui/icons-material/Mic';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import BlockIcon from '@mui/icons-material/Block';
import { theme } from '../theme';
import '../assets/styles/LiveDiarization.scss';

// Define types for the transcription and intent data
interface TranscriptionData {
  type: string;
  speaker_id: string;
  text: string;
  timestamp: number;
}

interface IntentAnalysisData {
  intents: {[key: string]: number};
  suggestedActions?: string[];
}

interface SubAction {
  id: string;
  text: string;
  completed: boolean;
  relatedField?: string; // Form field this sub-action is related to
}

interface DisputeFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  accountNumber: string;
  transactionDate: string;
  transactionAmount: string;
  merchantName: string;
  disputeReason: string;
  additionalNotes: string;
  disputeSummary: string;
}

interface CardRequestFormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  dateOfBirth: string;
  lastFourSSN: string;
  accountNumber: string;
  reason: string;
  expediteDelivery: boolean;
  deliveryAddress: string;
  additionalNotes: string;
  disputeSummary: string;
}

interface FormData {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  dateOfBirth: string;
  lastFourSSN: string;
  accountNumber: string;
  issueDescription: string;
  requestType: string;
  priority: string;
  additionalNotes: string;
  disputeSummary: string;
  expediteDelivery: boolean;
}

interface ServerDisputeFormData {
  transactionDate: string;
  transactionAmount: string;
  merchantName: string;
  disputeReason: string;
  description: string;
}

interface PrefillFormData {
  form: FormData;
  disputeForm?: ServerDisputeFormData;
}

interface UploadResponse {
  session_id: string;
  file_path: string;
}

interface CredentialsStatus {
  configured: boolean;
  speech_api: boolean;
  openai_api: boolean;
  details: {
    speech_key: boolean;
    speech_region: boolean;
    openai_key: boolean;
    openai_endpoint: boolean;
  };
}

function LiveDiarization({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild || { mode: 'light' };
  
  // States for managing the connection and data
  const [socket, setSocket] = useState<Socket | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isTranscribing, setIsTranscribing] = useState<boolean>(false);
  const [transcriptions, setTranscriptions] = useState<TranscriptionData[]>([]);
  const [intents, setIntents] = useState<{[key: string]: number}>({});
  const [suggestedActions, setSuggestedActions] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    dateOfBirth: '',
    lastFourSSN: '',
    accountNumber: '',
    issueDescription: '',
    requestType: '',
    priority: '',
    additionalNotes: '',
    disputeSummary: '',
    expediteDelivery: false
  });
  const [disputeFormData, setDisputeFormData] = useState<DisputeFormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    accountNumber: '',
    transactionDate: '',
    transactionAmount: '',
    merchantName: '',
    disputeReason: '',
    additionalNotes: '',
    disputeSummary: ''
  });
  const [cardRequestFormData, setCardRequestFormData] = useState<CardRequestFormData>({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerAddress: '',
    dateOfBirth: '',
    lastFourSSN: '',
    accountNumber: '',
    reason: '',
    expediteDelivery: false,
    deliveryAddress: '',
    additionalNotes: '',
    disputeSummary: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0.5); // Volume from 0 to 1
  const [currentTime, setCurrentTime] = useState<string>("00:00");
  const [duration, setDuration] = useState<string>("00:00");
  const [progress, setProgress] = useState<number>(0);
  const [credentialsStatus, setCredentialsStatus] = useState<CredentialsStatus | null>(null);
  const [credentialsChecked, setCredentialsChecked] = useState<boolean>(false);
  const [socketStatus, setSocketStatus] = useState<any>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [formVisible, setFormVisible] = useState(false);
  const [activeFormType, setActiveFormType] = useState<string | null>(null);
  const [clickedActions, setClickedActions] = useState<{[key: string]: string}>({});
  
  // New states for tracking sub-actions
  const [subActions, setSubActions] = useState<{[key: string]: SubAction[]}>({});
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [showSubActions, setShowSubActions] = useState<boolean>(false);
  const [disputeFormComplete, setDisputeFormComplete] = useState<boolean>(false);
  const [cardFormComplete, setCardFormComplete] = useState<boolean>(false);
  const [showSubmissionConfirmation, setShowSubmissionConfirmation] = useState<boolean>(false);
  
  // Add a state for tracking the previously returned JSON
  const [prevAnalysisJson, setPrevAnalysisJson] = useState<string>('');
  
  // Add a new state for storing the server-side file path
  const [serverFilePath, setServerFilePath] = useState<string | null>(null);
  
  // New states for form submission and visibility
  const [submittedForms, setSubmittedForms] = useState<{[key: string]: boolean}>({});
  const [formSubmissionSuccess, setFormSubmissionSuccess] = useState<boolean | null>(null);
  const [workflowState, setWorkflowState] = useState<string>("initial"); // "initial", "dispute", "block", "replace"
  const [callSummary, setCallSummary] = useState<string>("");
  const [summaryTimer, setSummaryTimer] = useState<number>(0);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate available height for cards
  const cardHeight = 'calc(100vh - 110px)';
  
  // Define audio control section height - make it thinner
  const audioControlsHeight = 180; // Reduced height to take less space

  // Group transcriptions by speaker
  const transcriptionsByPerson = transcriptions.reduce((groups, transcript) => {
    const speakerId = transcript.speaker_id || 'Unknown';
    if (!groups[speakerId]) {
      groups[speakerId] = [];
    }
    groups[speakerId].push(transcript);
    return groups;
  }, {} as {[key: string]: TranscriptionData[]});

  // Sort all transcriptions by timestamp for chronological display
  const sortedTranscriptions = [...transcriptions].sort((a, b) => a.timestamp - b.timestamp);

  // Ref for transcription autoscroll
  const transcriptionEndRef = useRef<HTMLDivElement | null>(null);

  // Ref for transcription container
  const transcriptionContainerRef = useRef<HTMLDivElement | null>(null);

  // Autoscroll transcription to bottom on new message
  useEffect(() => {
    if (transcriptionEndRef.current && transcriptionContainerRef.current) {
      // Use scrollTop to scroll just the container, not the whole page
      transcriptionContainerRef.current.scrollTop = transcriptionContainerRef.current.scrollHeight;
    }
  }, [sortedTranscriptions.length]);

  // Audio setup effect
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;
      audio.volume = volume;
      audio.muted = isMuted;
      
      const handleTimeUpdate = () => {
        if (audio.duration) {
          const progressPercent = (audio.currentTime / audio.duration) * 100;
          setProgress(progressPercent);
          
          const formatTime = (time: number) => {
            const minutes = Math.floor(time / 60);
            const seconds = Math.floor(time % 60);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          };
          
          setCurrentTime(formatTime(audio.currentTime));
          setDuration(formatTime(audio.duration));
        }
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
      };
      
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      
      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [audioUrl, volume, isMuted]);

  // Update sub-actions when form data changes
  useEffect(() => {
    if (activeFormType === 'Dispute Form') {
      checkDisputeFormCompletion();
    } else if (activeFormType === 'Card Request Form') {
      checkCardFormCompletion();
    }
    
    updateSubActionStatus();
  }, [disputeFormData, cardRequestFormData, formData, activeFormType]);

  // Check Socket.IO server status
  useEffect(() => {
    const checkSocketStatus = async () => {
      try {
        const response = await fetch('/socket_io_status');
        if (response.ok) {
          const data = await response.json();
          setSocketStatus(data);
          console.log('Socket.IO server status:', data);
          
          // Set appropriate error message if there's an issue
          if (data.status !== 'ok') {
            setConnectionError('Socket.IO server not properly initialized');
          } else if (data.connected_clients === 0) {
            // This is normal at first, don't set error immediately
            console.log('No clients connected yet');
          }
        } else {
          console.error('Failed to check Socket.IO status:', response.statusText);
          setConnectionError('Could not verify Socket.IO server status');
        }
      } catch (error) {
        console.error('Error checking Socket.IO status:', error);
        setConnectionError('Error connecting to server');
      }
    };

    checkSocketStatus();
    // Check periodically
    const intervalId = setInterval(checkSocketStatus, 30000);
    return () => clearInterval(intervalId);
  }, []);

  // Check if Azure credentials are configured
  useEffect(() => {
    const checkCredentials = async () => {
      try {
        const response = await fetch('/check_diarization_credentials');
        if (response.ok) {
          const data = await response.json();
          setCredentialsStatus(data);
          console.log('Credentials status:', data);
        } else {
          console.error('Failed to check credentials:', response.statusText);
        }
      } catch (error) {
        console.error('Error checking credentials:', error);
      } finally {
        setCredentialsChecked(true);
      }
    };

    checkCredentials();
  }, []);

  // Initialize Socket.IO connection
  useEffect(() => {
    // For Azure deployment
    const isProduction = window.location.hostname.includes('azurewebsites.net');
    let socketUrl;
    
    if (isProduction) {
      // Use the same URL as the web app for the WebSocket connection on Azure
      // Azure Web Apps handles WebSocket routing automatically when properly configured
      socketUrl = window.location.origin;
      console.log(`Using production Socket.IO connection to: ${socketUrl}`);
    } else {
      // Development setup
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = window.location.port || (protocol === 'wss:' ? '443' : '5000');
      socketUrl = `${protocol}//${host}:${port}`;
      console.log(`Using development Socket.IO connection to: ${socketUrl}`);
    }
    
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],  // Try websocket first, then fall back to polling
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true,
      path: '/socket.io' // Explicit path for clarity
    });

    newSocket.on('connect', () => {
      console.log('Socket connected successfully with ID:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
    });

    newSocket.on('connect_timeout', () => {
      console.error('Socket connection timeout');
      setIsConnected(false);
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log(`Socket reconnected after ${attemptNumber} attempts`);
      setIsConnected(true);
    });

    newSocket.on('reconnect_error', (error) => {
      console.error('Socket reconnection error:', error.message);
    });

    newSocket.on('disconnect', (reason) => {
      console.log(`Socket disconnected: ${reason}`);
      setIsConnected(false);
    });

    newSocket.on('transcription_update', (data: TranscriptionData) => {
      console.log('Received transcription update:', data);
      if (data.type === 'transcribed') {
        setTranscriptions(prev => [...prev, data]);
      }
    });

    newSocket.on('intent_analysis', (data: IntentAnalysisData) => {
      console.log('Intent analysis:', data);
      // Store the current analysis JSON for future LLM calls
      const jsonStr = JSON.stringify(data);
      setPrevAnalysisJson(jsonStr);
      
      setIntents(data.intents);
      if (data.suggestedActions) {
        setSuggestedActions(data.suggestedActions);
      }
    });

    newSocket.on('prefill_form', (data: { session_id: string, form: FormData, disputeForm?: ServerDisputeFormData }) => {
      if (data.session_id === sessionId) {
        console.log('Received prefilled form data:', data);
        
        // Update general form data
        setFormData(prev => {
          // Only update fields that are empty or if new data is not empty
          const updatedForm = { ...prev };
          
          // Handle string fields
          const stringFields = ['customerName', 'customerEmail', 'customerPhone', 'customerAddress', 'dateOfBirth', 'lastFourSSN', 'accountNumber', 'issueDescription', 'requestType', 'priority', 'additionalNotes', 'disputeSummary'] as const;
          
          stringFields.forEach(field => {
            if (!prev[field] && data.form[field]) {
              updatedForm[field] = data.form[field] as string;
            }
          });
          
          // Handle boolean field separately
          if (data.form.expediteDelivery !== undefined) {
            updatedForm.expediteDelivery = data.form.expediteDelivery as boolean;
          }
          
          return updatedForm;
        });
        
        // Also update dispute form with relevant fields
        setDisputeFormData(prev => {
          // Start with current data
          const updatedForm = { ...prev };
          
          // Update basic customer info fields from main form if they're empty
          if (!prev.customerName && data.form.customerName) {
            updatedForm.customerName = data.form.customerName;
          }
          if (!prev.customerEmail && data.form.customerEmail) {
            updatedForm.customerEmail = data.form.customerEmail;
          }
          if (!prev.customerPhone && data.form.customerPhone) {
            updatedForm.customerPhone = data.form.customerPhone;
          }
          if (!prev.accountNumber && data.form.accountNumber) {
            updatedForm.accountNumber = data.form.accountNumber;
          }
          if (data.form.disputeSummary) {
            updatedForm.disputeSummary = data.form.disputeSummary;
          }
          
          // Update dispute-specific fields if they exist
          if (data.disputeForm) {
            if (!prev.transactionDate && data.disputeForm.transactionDate) {
              updatedForm.transactionDate = data.disputeForm.transactionDate;
            }
            if (!prev.transactionAmount && data.disputeForm.transactionAmount) {
              updatedForm.transactionAmount = data.disputeForm.transactionAmount;
            }
            if (!prev.merchantName && data.disputeForm.merchantName) {
              updatedForm.merchantName = data.disputeForm.merchantName;
            }
            if (!prev.disputeReason && data.disputeForm.disputeReason) {
              updatedForm.disputeReason = data.disputeForm.disputeReason;
            }
          }
          
          return updatedForm;
        });
        
        // Update card request form with relevant fields
        setCardRequestFormData(prev => {
          // Only update fields that are empty or if new data is not empty
          const updatedForm = { ...prev };
          
          if (!prev.customerName && data.form.customerName) {
            updatedForm.customerName = data.form.customerName;
          }
          if (!prev.customerEmail && data.form.customerEmail) {
            updatedForm.customerEmail = data.form.customerEmail;
          }
          if (!prev.customerPhone && data.form.customerPhone) {
            updatedForm.customerPhone = data.form.customerPhone;
          }
          if (!prev.customerAddress && data.form.customerAddress) {
            updatedForm.customerAddress = data.form.customerAddress;
          }
          if (!prev.dateOfBirth && data.form.dateOfBirth) {
            updatedForm.dateOfBirth = data.form.dateOfBirth;
          }
          if (!prev.lastFourSSN && data.form.lastFourSSN) {
            updatedForm.lastFourSSN = data.form.lastFourSSN;
          }
          if (!prev.accountNumber && data.form.accountNumber) {
            updatedForm.accountNumber = data.form.accountNumber;
          }
          if (!prev.disputeSummary && data.form.disputeSummary) {
            updatedForm.disputeSummary = data.form.disputeSummary;
          }
          if (data.form.expediteDelivery !== undefined) {
            updatedForm.expediteDelivery = data.form.expediteDelivery;
          }
          
          return updatedForm;
        });
      }
    });

    // Listen for call summary
    newSocket.on('call_summary', (data: { session_id: string, callSummary: string }) => {
      console.log('Received call summary event:', data);
      if (data.session_id === sessionId) {
        console.log('Call summary matches session, updating state with:', data.callSummary);
        setCallSummary(data.callSummary);
        
        // Stop timer and loading state
        setIsGeneratingSummary(false);
        setSummaryTimer(0);
        
        // Clear the timer interval
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        // Update the dispute summary field in all forms
        setFormData(prev => ({
          ...prev,
          disputeSummary: data.callSummary,
          additionalNotes: data.callSummary
        }));
        
        setDisputeFormData(prev => ({
          ...prev,
          disputeSummary: data.callSummary,
          additionalNotes: data.callSummary
        }));
        
        setCardRequestFormData(prev => ({
          ...prev,
          disputeSummary: data.callSummary,
          additionalNotes: data.callSummary
        }));
      } else {
        console.warn('Received call summary for different session', {
          receivedSession: data.session_id,
          currentSession: sessionId
        });
      }
    });

    newSocket.on('form_submission_response', (data: { success: boolean, message: string }) => {
      console.log('Form submission response:', data);
      setFormSubmissionSuccess(data.success);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setFormSubmissionSuccess(null);
      }, 3000);
    });

    newSocket.on('error', (error: Error) => {
      console.error('Socket error:', error);
    });

    setSocket(newSocket);

    return () => {
      console.log('Disconnecting socket');
      newSocket.disconnect();
    };
  }, [sessionId]);

  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'audio/wav') {
        setSelectedFile(file);
        // Create audio URL for playback
        const url = URL.createObjectURL(file);
        setAudioUrl(url);
        // Reset audio states
        setIsPlaying(false);
        setIsMuted(false);
        setVolume(0.5);
      } else {
        alert('Please upload a WAV file.');
      }
    }
  };

  // Start transcription with current file
  const handleStartTranscription = () => {
    if (selectedFile && !isUploading && isSystemReady) {
      handleUpload();
    } else if (socket && sessionId && serverFilePath) {
      console.log('Starting transcription with session ID:', sessionId);
      socket.emit('start_transcription', {
        session_id: sessionId,
        file_path: serverFilePath
      });
      setIsTranscribing(true);
      setTranscriptions([]);
      setIntents({});
      setSuggestedActions([]);
      setClickedActions({});
    }
  };

  // Stop current transcription
  const handleStopTranscription = () => {
    if (socket && sessionId) {
      console.log('Stopping transcription for session:', sessionId);
      socket.emit('stop_transcription', { session_id: sessionId });
      setIsTranscribing(false);
      
      // Request call summary after transcription stops
      setTimeout(() => {
        requestCallSummary();
      }, 1000);
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', selectedFile);
    
    try {
      const url = '/upload_audio';
      console.log('Uploading file to:', url);
      
      // Upload file to server
      const response = await fetch(url, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.status} ${response.statusText}`);
      }
      
      const data: UploadResponse = await response.json();
      console.log('Upload successful:', data);
      
      // Store the file path from the server response
      setServerFilePath(data.file_path);
      
      // Start transcription session
      if (socket && data.session_id) {
        setSessionId(data.session_id);
        console.log('Starting transcription with session ID:', data.session_id);
        socket.emit('start_transcription', {
          session_id: data.session_id,
          file_path: data.file_path
        });
        setIsTranscribing(true);
        setTranscriptions([]);
        setIntents({});
        setSuggestedActions([]);
        setClickedActions({});
      } else {
        console.error('Socket not connected or missing session ID');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload file. Check console for details.');
    } finally {
      setIsUploading(false);
      setUploadProgress(100);
    }
  };

  // Trigger file selection dialog
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Audio control functions
  const handleVolumeUp = () => {
    const newVolume = Math.min(volume + 0.1, 1);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleVolumeDown = () => {
    const newVolume = Math.max(volume - 0.1, 0);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleMuteToggle = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Sort intents by confidence (highest first)
  const sortedIntents = Object.entries(intents)
    .sort(([, valueA], [, valueB]) => valueB - valueA)
    .slice(0, 5); // Limit to top 5 intents

  // Determine if the system is ready for diarization
  const isSystemReady = isConnected && credentialsStatus?.configured;

  // Get appropriate text color based on mode
  const textColor = mode === 'dark' ? theme.colors.darkModeText : theme.colors.text;

  // Generate sub-actions for the selected action
  const generateSubActions = (action: string): SubAction[] => {
    if (action.toLowerCase().includes('dispute')) {
      return [
        { id: 'dispute-1', text: 'Confirm customer identity', completed: false, relatedField: 'customerName' },
        { id: 'dispute-2', text: 'Get customer contact information', completed: false, relatedField: 'customerPhone' },
        { id: 'dispute-3', text: 'Verify account number', completed: false, relatedField: 'accountNumber' },
        { id: 'dispute-4', text: 'Record transaction date', completed: false, relatedField: 'transactionDate' },
        { id: 'dispute-5', text: 'Record transaction amount', completed: false, relatedField: 'transactionAmount' },
        { id: 'dispute-6', text: 'Document merchant name', completed: false, relatedField: 'merchantName' },
        { id: 'dispute-7', text: 'Determine dispute reason', completed: false, relatedField: 'disputeReason' }
      ];
    } else if (action.toLowerCase().includes('card') && 
              (action.toLowerCase().includes('replace') || 
               action.toLowerCase().includes('request') ||
               action.toLowerCase().includes('replacement'))) {
      return [
        { id: 'card-1', text: 'Confirm customer identity', completed: false, relatedField: 'customerName' },
        { id: 'card-2', text: 'Verify date of birth', completed: false, relatedField: 'dateOfBirth' },
        { id: 'card-3', text: 'Verify last 4 of SSN', completed: false, relatedField: 'lastFourSSN' },
        { id: 'card-4', text: 'Confirm account number', completed: false, relatedField: 'accountNumber' },
        { id: 'card-5', text: 'Confirm shipping address', completed: false, relatedField: 'customerAddress' },
        { id: 'card-6', text: 'Ask about expedited delivery', completed: false, relatedField: 'expediteDelivery' }
      ];
    } else if (action.toLowerCase().includes('block')) {
      // Return empty array for Block Card - no steps needed
      return [];
    } else {
      return [
        { id: 'general-1', text: 'Collect customer information', completed: false },
        { id: 'general-2', text: 'Verify customer identity', completed: false },
        { id: 'general-3', text: 'Document customer issue', completed: false },
        { id: 'general-4', text: 'Explain next steps to customer', completed: false }
      ];
    }
  };

  // Check if dispute form is complete
  const checkDisputeFormCompletion = () => {
    const requiredFields = [
      'customerName', 
      'customerPhone', 
      'accountNumber',
      'transactionDate',
      'transactionAmount',
      'merchantName',
      'disputeReason'
    ];
    
    const isComplete = requiredFields.every(field => 
      disputeFormData[field as keyof DisputeFormData] !== ''
    );
    
    setDisputeFormComplete(isComplete);
    return isComplete;
  };

  // Check if card request form is complete
  const checkCardFormCompletion = () => {
    const requiredFields = [
      'customerName',
      'customerPhone',
      'customerAddress',
      'dateOfBirth',
      'lastFourSSN',
      'accountNumber',
      'reason'
    ];
    
    const isComplete = requiredFields.every(field => 
      cardRequestFormData[field as keyof CardRequestFormData] !== ''
    );
    
    setCardFormComplete(isComplete);
    return isComplete;
  };

  // Update sub-action completion based on form data
  const updateSubActionStatus = () => {
    if (!selectedAction) return;
    
    setSubActions(prevSubActions => {
      const actionSubActions = [...(prevSubActions[selectedAction] || [])];
      
      // Update completion status based on form fields
      return {
        ...prevSubActions,
        [selectedAction]: actionSubActions.map(subAction => {
          if (!subAction.relatedField) return subAction;
          
          let isCompleted = false;
          
          if (activeFormType === 'Dispute Form') {
            const value = disputeFormData[subAction.relatedField as keyof DisputeFormData];
            isCompleted = value !== undefined && value !== '';
          } else if (activeFormType === 'Card Request Form') {
            const value = cardRequestFormData[subAction.relatedField as keyof CardRequestFormData];
            isCompleted = value !== undefined && value !== '' && value !== false;
          } else {
            const value = formData[subAction.relatedField as keyof FormData];
            isCompleted = value !== undefined && value !== '';
          }
          
          return { ...subAction, completed: isCompleted };
        })
      };
    });
  };

  // Display connection status alerts
  const renderStatusAlerts = () => {
    if (!credentialsChecked || !credentialsStatus?.configured || !isConnected) {
      return (
        <Box className="alert-container">
          {credentialsChecked && !credentialsStatus?.configured && (
            <Alert severity="error" className="status-alert">
              Azure credentials not properly configured. Please check your configuration.
              {credentialsStatus && (
                <ul className="error-list">
                  {!credentialsStatus.speech_api && (
                    <li>Azure Speech API credentials are missing</li>
                  )}
                  {!credentialsStatus.openai_api && (
                    <li>Azure OpenAI API credentials are missing</li>
                  )}
                </ul>
              )}
            </Alert>
          )}

          {!isConnected && (
            <Alert severity="warning" className="status-alert">
              Socket.IO connection to server failed. Please check if the server is running.
              {connectionError && (
                <div className="error-detail">
                  <strong>Error details:</strong> {connectionError}
                </div>
              )}
            </Alert>
          )}
        </Box>
      );
    }
    return null;
  };

  // Handle form field change
  const handleFormChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle dispute form field change
  const handleDisputeFormChange = (field: keyof DisputeFormData, value: string) => {
    setDisputeFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check form completion after a slight delay to allow state update
    setTimeout(() => {
      checkDisputeFormCompletion();
      updateSubActionStatus();
    }, 50);
  };

  // Handle card request form field change
  const handleCardRequestFormChange = (field: keyof CardRequestFormData, value: string | boolean) => {
    setCardRequestFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Check form completion after a slight delay to allow state update
    setTimeout(() => {
      checkCardFormCompletion();
      updateSubActionStatus();
    }, 50);
  };

  // Submit form data with email
  const handleFormSubmit = () => {
    console.log('Submitting form data:', formData);
    
    if (socket && sessionId) {
      socket.emit('submit_form', {
        session_id: sessionId,
        form_type: 'General Form',
        form_data: formData
      });
    }
    
    setFormSubmitted(true);
    setShowSubmissionConfirmation(true);
    
    // Mark this form as submitted
    setSubmittedForms(prev => ({
      ...prev,
      'general': true
    }));
    
    // Update action status to completed
    if (selectedAction) {
      setClickedActions(prev => ({
        ...prev,
        [selectedAction]: 'completed'
      }));
    }
    
    setTimeout(() => {
      setFormSubmitted(false);
      setShowSubmissionConfirmation(false);
      setFormVisible(false);
      setSelectedAction(null);
      setShowSubActions(false);
    }, 3000);
  };

  // Submit dispute form data with email
  const handleDisputeFormSubmit = () => {
    console.log('Submitting dispute form data:', disputeFormData);
    
    if (socket && sessionId) {
      socket.emit('submit_form', {
        session_id: sessionId,
        form_type: 'Dispute Form',
        form_data: disputeFormData
      });
    }
    
    setFormSubmitted(true);
    setShowSubmissionConfirmation(true);
    
    // Mark this form as submitted
    setSubmittedForms(prev => ({
      ...prev,
      'dispute': true
    }));
    
    // Update action status to completed
    if (selectedAction) {
      setClickedActions(prev => ({
        ...prev,
        [selectedAction]: 'completed'
      }));
    }
    
    // Update workflow state
    setWorkflowState("block");
    
    setTimeout(() => {
      setFormSubmitted(false);
      setShowSubmissionConfirmation(false);
      setFormVisible(false);
      setSelectedAction(null);
      setShowSubActions(false);
    }, 3000);
  };

  // Submit card request form data with email
  const handleCardRequestFormSubmit = () => {
    console.log('Submitting card request form data:', cardRequestFormData);
    
    if (socket && sessionId) {
      socket.emit('submit_form', {
        session_id: sessionId,
        form_type: 'Card Request Form',
        form_data: cardRequestFormData
      });
    }
    
    setFormSubmitted(true);
    setShowSubmissionConfirmation(true);
    
    // Mark this form as submitted
    setSubmittedForms(prev => ({
      ...prev,
      'card': true
    }));
    
    // Update action status to completed
    if (selectedAction) {
      setClickedActions(prev => ({
        ...prev,
        [selectedAction]: 'completed'
      }));
    }
    
    // Update workflow state
    setWorkflowState("completed");
    
    setTimeout(() => {
      setFormSubmitted(false);
      setShowSubmissionConfirmation(false);
      setFormVisible(false);
      setSelectedAction(null);
      setShowSubActions(false);
    }, 3000);
  };

  // Request call summary
  const requestCallSummary = () => {
    if (socket && sessionId) {
      socket.emit('get_call_summary', {
        session_id: sessionId
      });
    }
  };

  // When a suggested action is clicked
  const handleSuggestedActionClick = (action: string) => {
    console.log("Action clicked:", action);
    // Always allow showing the form regardless of previous submissions
    setFormVisible(true);
    setSelectedAction(action);
    setShowSubActions(true);
    
    // Generate sub-actions for this action if not already generated
    if (!subActions[action]) {
      const newSubActions = generateSubActions(action);
      setSubActions(prev => ({
        ...prev,
        [action]: newSubActions
      }));
    }
    
    // Determine form type based on action text
    if (action.toLowerCase().includes('dispute')) {
      setActiveFormType('Dispute Form');
      checkDisputeFormCompletion();
      
      // If dispute form action is clicked, add fraud intent with high confidence
      if (action.toLowerCase().includes('dispute')) {
        setIntents(prev => ({
          ...prev,
          "Fraud Report": 0.95
        }));
      }
    } else if (action.toLowerCase().includes('block')) {
      console.log("Setting active form type to Block Card");
      setActiveFormType('Block Card');
    } else if (action.toLowerCase().includes('card') && 
              (action.toLowerCase().includes('replace') || 
               action.toLowerCase().includes('request') ||
               action.toLowerCase().includes('replacement'))) {
      console.log("Setting active form type to Card Request Form");
      setActiveFormType('Card Request Form');
      checkCardFormCompletion();
    } else {
      setActiveFormType('General Form');
    }
    
    // Update sub-action status based on current form data
    setTimeout(updateSubActionStatus, 50);
  };

  // Add a function to manually request call summary
  const handleGenerateDisputeSummary = () => {
    if (socket && sessionId) {
      console.log('Manually requesting dispute summary for session:', sessionId);
      
      // Start timer and loading state
      setIsGeneratingSummary(true);
      setSummaryTimer(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setSummaryTimer(prev => prev + 1);
      }, 1000);
      
      // Add immediate feedback
      setCallSummary("Generating summary...");
      
      // Auto-fill additional notes with generating message
      setFormData(prev => ({
        ...prev,
        additionalNotes: "AI is generating summary... Please wait."
      }));
      
      setDisputeFormData(prev => ({
        ...prev,
        additionalNotes: "AI is generating summary... Please wait."
      }));
      
      setCardRequestFormData(prev => ({
        ...prev,
        additionalNotes: "AI is generating summary... Please wait."
      }));
      
      socket.emit('get_call_summary', {
        session_id: sessionId
      });
    } else {
      console.error('Cannot generate summary: socket or sessionId is missing', { socket: !!socket, sessionId });
    }
  };

  return (
    <div className="live-diarization-root">
      {renderStatusAlerts()}

      {/* Connection Status Panel */}
      <Grid item xs={12}>
        <Paper elevation={3} className="status-panel">
          <Box className="connection-status">
            <Typography variant="subtitle1" className="status-label" sx={{ fontSize: '1.1rem' }}>
              Connection Status: 
            </Typography>
            <Typography variant="subtitle1" className={`status-value ${isConnected ? 'connected' : 'disconnected'}`} sx={{ fontSize: '1.1rem' }}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </Typography>
          </Box>
          {isTranscribing && (
            <Box className="live-indicator">
              <FiberManualRecordIcon className="recording-icon" sx={{ fontSize: '1.2rem' }} />
              <Typography variant="subtitle1" sx={{ fontSize: '1.1rem' }}>LIVE</Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* New 3-column grid layout */}
      <Grid container spacing={3}>
        {/* Left Column - Transcription */}
        <Grid item xs={12} md={4} lg={4}>
          <Paper elevation={4} className="dashboard-card" sx={{ height: cardHeight, minHeight: 500, display: 'flex', flexDirection: 'column' }}>
            <Box className="card-header">
              <Box className="card-title-container">
                <Typography variant="h5" component="h2" className="card-title">
                  Transcription
                </Typography>
                <StarIcon className="star-icon" sx={{ color: theme.colors.primary }} />
              </Box>
            </Box>
            
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              p: 0
            }}>
              {/* File Upload Section Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Upload Audio File
                </Typography>
              </Box>
              
              {/* File Upload Section */}
              <Box sx={{ 
                p: 2, 
                pb: 1,
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <input 
                  type="file"
                  accept=".wav"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                />
                
                {!selectedFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box 
                      className="upload-box" 
                      onClick={openFileDialog}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        width: '100%',
                        border: '2px dashed #aaa',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0,0,0,0.04)',
                        '&:hover': {
                          borderColor: theme.colors.primary,
                          backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.05)`
                        }
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.9rem' }}>
                          Drag and drop or click to browse
                        </Typography>
                        <Button 
                          variant="contained" 
                          size="small"
                          sx={{ 
                            mt: 1, 
                            bgcolor: theme.colors.primary, 
                            color: theme.colors.secondary,
                            fontSize: '0.8rem',
                            '&:hover': { bgcolor: theme.colors.primary, filter: 'brightness(0.9)' }
                          }}
                        >
                          SELECT FILE
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.9rem', mb: 1, fontWeight: 'medium' }}>
                      {selectedFile.name}
                    </Typography>
                    
                    <Box sx={{ mt: 1, mb: 1 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={isTranscribing ? handleStopTranscription : handleStartTranscription}
                        disabled={!selectedFile || !isSystemReady || isUploading}
                        sx={{
                          bgcolor: isTranscribing ? '#f44336' : theme.colors.primary,
                          color: theme.colors.secondary,
                          '&:hover': {
                            bgcolor: isTranscribing ? '#d32f2f' : theme.colors.primary,
                            filter: 'brightness(0.9)'
                          }
                        }}
                      >
                        {isTranscribing ? 'Stop Transcription' : 'Start Transcription'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
              
              {/* Audio Controls Section */}
              {selectedFile && audioUrl && (
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HeadphonesIcon sx={{ color: theme.colors.primary, fontSize: '1.2rem' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                      Audio Controls
                    </Typography>
                  </Box>
                  
                  {/* Audio Player Controls */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    {/* Play/Pause Button */}
                    <IconButton 
                      onClick={handlePlayPause}
                      sx={{ 
                        color: theme.colors.primary,
                        '&:hover': { backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)` }
                      }}
                    >
                      {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                    
                    {/* Volume Controls */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton 
                        onClick={handleVolumeDown}
                        size="small"
                        sx={{ 
                          color: theme.colors.primary,
                          '&:hover': { backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)` }
                        }}
                      >
                        <VolumeOffIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                      
                      <IconButton 
                        onClick={handleMuteToggle}
                        size="small"
                        sx={{ 
                          color: isMuted ? '#f44336' : theme.colors.primary,
                          '&:hover': { backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)` }
                        }}
                      >
                        {isMuted ? <VolumeOffIcon sx={{ fontSize: '1rem' }} /> : <VolumeUpIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                      
                      <IconButton 
                        onClick={handleVolumeUp}
                        size="small"
                        sx={{ 
                          color: theme.colors.primary,
                          '&:hover': { backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)` }
                        }}
                      >
                        <VolumeUpIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                      
                      <Typography variant="body2" sx={{ ml: 1, minWidth: '35px', fontSize: '0.85rem' }}>
                        {Math.round(volume * 100)}%
                      </Typography>
                    </Box>
                    
                    {/* Time Display */}
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                      {currentTime} / {duration}
                    </Typography>
                  </Box>
                  
                  {/* Progress Bar */}
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: theme.colors.primary,
                      }
                    }}
                  />
                  
                  {/* Hidden Audio Element */}
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    style={{ display: 'none' }}
                  />
                </Box>
              )}
              
              {/* Transcription Section Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)', display: 'flex', alignItems: 'center', gap: 1 }}>
                <MicIcon sx={{ color: theme.colors.primary, fontSize: '1.2rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Live Transcription
                </Typography>
              </Box>
              
              {/* Transcription Content - add ref to container */}
              <Box 
                ref={transcriptionContainerRef}
                className="transcription-container" 
                sx={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  p: 2,
                  minHeight: 0 
                }}
              >
                {sortedTranscriptions.map((message, idx) => {
                  const speakerId = message.speaker_id || 'Unknown';
                  const isAgent = speakerId.includes('Guest-1');
                  const isCustomer = speakerId.includes('Guest-2');
                  return (
                    <Box key={idx} sx={{ mb: 3 }}>
                      <Typography 
                        variant="subtitle1" 
                        className={`speaker-label ${isAgent ? 'agent-label' : 'customer-label'}`}
                        sx={{ 
                          textAlign: isAgent ? 'left' : 'right', 
                          fontSize: '0.9rem', 
                          fontWeight: 600, 
                          mb: 0.5,
                          color: isAgent ? theme.colors.secondary : '#ff9800' // Orange for customer label
                        }}
                      >
                        {isAgent ? 'Agent' : isCustomer ? 'Customer' : 'Unknown'}
                      </Typography>
                      <Box 
                        className={`message ${isAgent ? 'agent-message' : 'customer-message'}`}
                        sx={{
                          display: 'flex',
                          justifyContent: isAgent ? 'flex-start' : 'flex-end',
                        }}
                      >
                        <Box sx={{
                          maxWidth: '85%',
                          p: 1.5,
                          borderRadius: isAgent ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
                          backgroundColor: isAgent ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 152, 0, 0.1)', // Orange for customer
                          border: isAgent ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255, 152, 0, 0.2)', // Orange border for customer
                        }}>
                          <Typography variant="body1" sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                            {message.text}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                {transcriptions.length === 0 && !isTranscribing && (
                  <Typography className="empty-state" sx={{ fontSize: '1rem', mt: 4, textAlign: 'center', color: textColor }}>
                    Upload an audio file to start transcription
                  </Typography>
                )}
                {transcriptions.length === 0 && isTranscribing && (
                  <Typography className="empty-state" sx={{ fontSize: '1rem', mt: 4, textAlign: 'center', color: textColor }}>
                    Waiting for speech...
                  </Typography>
                )}
                
                {/* Call Summary */}
                {callSummary && !isTranscribing && transcriptions.length > 0 && (
                  <Box sx={{ 
                    mt: 4, 
                    mb: 2, 
                    p: 3, 
                    backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                    borderRadius: 2,
                    border: '1px solid rgba(0, 0, 0, 0.09)'
                  }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.colors.primary }}>
                      Call Summary
                    </Typography>
                    <Typography variant="body1">
                      {callSummary}
                    </Typography>
                  </Box>
                )}
                
                {/* Autoscroll anchor */}
                <div ref={transcriptionEndRef} />
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Middle Column - Customer Intents & Agent Actions */}
        <Grid item xs={12} md={4} lg={4}>
          <Paper elevation={4} className="dashboard-card" sx={{ height: cardHeight, minHeight: 500, display: 'flex', flexDirection: 'column' }}>
            <Box className="card-header">
              <Box className="card-title-container">
                <Typography variant="h5" component="h2" className="card-title">
                  Customer Intents & Agent Actions
                </Typography>
                {mode === "dark" ? (
                  <span className="shine">
                    <AutoAwesomeIcon className="auto-awesome-icon" sx={{ color: theme.colors.primary }} />
                  </span>
                ) : (
                  <span className="ai-driven">
                    <AutoAwesomeIcon className="auto-awesome-icon" sx={{ color: theme.colors.primary }} />
                  </span>
                )}
              </Box>
            </Box>
            
            <Box sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              p: 0
            }}>
              
              {/* Customer Intents Section Header */}
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Top Customer Intents
                </Typography>
              </Box>
              
              {/* Customer Intents Content */}
              <Box sx={{ p: 2, flex: '0 0 auto' }}>
                {sortedIntents.length > 0 ? (
                  sortedIntents.slice(0, 3).map(([intent, confidence], idx) => (
                    <Box key={idx} className="intent-item" sx={{ mb: 2 }}>
                      <Box className="intent-header" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" className="intent-name" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                          {intent}
                        </Typography>
                        <Typography variant="subtitle1" className="intent-score" sx={{ fontSize: '1rem', fontWeight: 600, color: theme.colors.primary }}>
                          {Math.round(confidence * 100)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={confidence * 100}
                        className="intent-progress"
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.colors.primary,
                          }
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography className="empty-state" sx={{ fontSize: '1rem', textAlign: 'center', color: textColor }}>
                    No customer intents detected yet
                  </Typography>
                )}
              </Box>
              
              {/* Suggested Agent Actions Section */}
              <Box sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, 0.06)', borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                  Recommended Agent Actions
                </Typography>
              </Box>
              
              {/* Actions Content */}
              <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                {suggestedActions && suggestedActions.length > 0 ? (
                  <Box className="actions-list" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {suggestedActions.map((action, idx) => {
                      const actionStatus = clickedActions[action] || '';
                      const isClicked = actionStatus === 'clicked';
                      const isCompleted = actionStatus === 'completed';
                      
                      // Remove any conditions that would disable actions based on workflow state or completed status
                      // Allow clicking on any action at any time
                      
                      return (
                        <Box key={idx}>
                          <Paper 
                            elevation={0}
                            className={`action-item ${isClicked ? 'clicked' : ''} ${isCompleted ? 'completed' : ''}`}
                            onClick={() => handleSuggestedActionClick(action)}
                            sx={{ 
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: isCompleted ? 
                                `rgba(76, 175, 80, 0.15)` : 
                                isClicked ? 
                                  `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.15)` : 
                                  `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.05)`,
                              border: isCompleted ? 
                                '1px solid rgba(76, 175, 80, 0.3)' : 
                                isClicked ? 
                                  `1px solid rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.4)` : 
                                  `1px solid rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.2)`,
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)`,
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            {isCompleted ? (
                              <CheckCircleIcon sx={{ color: '#4caf50', mt: 0.5 }} />
                            ) : (
                              <LightbulbIcon sx={{ color: theme.colors.primary, mt: 0.5 }} />
                            )}
                            <Typography variant="body1" sx={{ fontSize: '0.95rem', lineHeight: 1.5 }}>
                              {action}
                            </Typography>
                          </Paper>
                          
                          {/* Sub-actions for the selected action */}
                          {selectedAction === action && showSubActions && subActions[action] && (
                            <Box sx={{ 
                              mt: 1, 
                              ml: 4, 
                              mb: 1,
                              p: 1.5,
                              borderLeft: `2px solid ${theme.colors.primary}`,
                              backgroundColor: 'rgba(0,0,0,0.02)'
                            }}>
                              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.colors.primary }}>
                                Agent Steps:
                              </Typography>
                              {subActions[action].map((subAction, subIdx) => (
                                <Box key={subIdx} sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 0.75,
                                  opacity: subAction.completed ? 0.6 : 1
                                }}>
                                  {subAction.completed ? (
                                    <CheckBoxIcon sx={{ color: theme.colors.primary, mr: 1, fontSize: '1.1rem' }} />
                                  ) : (
                                    <CheckBoxOutlineBlankIcon sx={{ color: theme.colors.primary, mr: 1, fontSize: '1.1rem', opacity: 0.7 }} />
                                  )}
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      textDecoration: subAction.completed ? 'line-through' : 'none',
                                      color: subAction.completed ? '#666' : 'inherit'
                                    }}
                                  >
                                    {subAction.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography className="empty-state" sx={{ fontSize: '1rem', textAlign: 'center', color: textColor }}>
                    No agent actions recommended yet
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* Right Column - Dynamic Forms */}
        <Grid item xs={12} md={4} lg={4}>
          <Paper elevation={4} className="dashboard-card" sx={{ height: cardHeight, minHeight: 500, display: 'flex', flexDirection: 'column' }}>
            {formVisible ? (
              <>
                <Box className="card-header">
                  <Box className="card-title-container">
                    <Typography variant="h5" component="h2" className="card-title">
                      {activeFormType || 'Generated Form'}
                    </Typography>
                    <AssignmentIcon className="assignment-icon" sx={{ color: theme.colors.primary }} />
                  </Box>
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  p: 0
                }}>
                  {/* Form Header */}
                  <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
                      {activeFormType === 'Dispute Form' ? 'Transaction Dispute' : 
                       activeFormType === 'Card Request Form' ? 'Card Replacement' : 
                       'Customer Information'}
                    </Typography>
                  </Box>
                  
                  {/* Form Content */}
                  <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                    {formSubmitted && (
                      <Alert severity="success" sx={{ mb: 2 }}>Form submitted successfully!</Alert>
                    )}
                    
                    {showSubmissionConfirmation && (
                      <Box 
                        sx={{ 
                          position: 'fixed',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 1000,
                          backgroundColor: 'white',
                          p: 4,
                          borderRadius: 2,
                          boxShadow: '0px 4px 20px rgba(0,0,0,0.25)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          minWidth: 300
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '3rem', mb: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>
                          Form Submitted Successfully
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
                          {activeFormType === 'Dispute Form' 
                            ? 'Dispute form has been submitted. A confirmation will be sent to the customer.' 
                            : activeFormType === 'Card Request Form'
                            ? 'Card replacement request has been processed. A new card will be issued.'
                            : activeFormType === 'Block Card'
                            ? 'Card has been blocked. The customer will be notified.'
                            : 'Your form has been submitted.'}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Dispute Form */}
                    {activeFormType === 'Dispute Form' && (
                      <Grid container spacing={2}>
                        {/* Dispute Summary at the top */}
                        <Grid item xs={12}>
                          <Paper elevation={1} sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                            borderLeft: `4px solid ${theme.colors.primary}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Dispute Summary
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AutoAwesomeIcon />}
                                onClick={handleGenerateDisputeSummary}
                                disabled={isGeneratingSummary}
                                sx={{ 
                                  bgcolor: theme.colors.primary, 
                                  color: mode === 'dark' ? '#ffffff' : theme.colors.secondary,
                                  '&:hover': {
                                    bgcolor: theme.colors.primary,
                                    filter: 'brightness(0.9)'
                                  }
                                }}
                              >
                                {isGeneratingSummary ? `Generating... ${summaryTimer}s` : 'Generate Summary'}
                              </Button>
                            </Box>
                            {isTranscribing ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Click "Generate Summary" to create a summary from the current transcript.
                              </Typography>
                            ) : callSummary ? (
                              <Typography variant="body2">
                                {callSummary}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                No summary available. Click "Generate Summary" button to create one.
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Customer Name"
                            value={disputeFormData.customerName}
                            onChange={(e) => handleDisputeFormChange('customerName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={disputeFormData.customerPhone}
                            onChange={(e) => handleDisputeFormChange('customerPhone', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={disputeFormData.accountNumber}
                            onChange={(e) => handleDisputeFormChange('accountNumber', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Transaction Date"
                            value={disputeFormData.transactionDate}
                            onChange={(e) => handleDisputeFormChange('transactionDate', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Transaction Amount"
                            value={disputeFormData.transactionAmount}
                            onChange={(e) => handleDisputeFormChange('transactionAmount', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Merchant Name"
                            value={disputeFormData.merchantName}
                            onChange={(e) => handleDisputeFormChange('merchantName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            label="Dispute Reason"
                            value={disputeFormData.disputeReason}
                            onChange={(e) => handleDisputeFormChange('disputeReason', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          >
                            {['Unauthorized Transaction', 'Duplicate Charge', 'Product Not Received', 'Product Defective', 'Incorrect Amount', 'Other'].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Additional Notes"
                            value={disputeFormData.additionalNotes}
                            onChange={(e) => handleDisputeFormChange('additionalNotes', e.target.value)}
                            variant="outlined"
                            multiline
                            rows={3}
                            sx={{ 
                              mb: 2,
                              '& .MuiInputBase-input': {
                                color: isGeneratingSummary ? theme.colors.primary : 'inherit'
                              }
                            }}
                            helperText={isGeneratingSummary ? `AI generating summary... ${summaryTimer}s elapsed` : ''}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button 
                              variant="contained" 
                              startIcon={<SendIcon />}
                              sx={{ 
                                bgcolor: theme.colors.primary, 
                                color: theme.colors.secondary,
                                '&:hover': {
                                  bgcolor: theme.colors.primary,
                                  filter: 'brightness(0.9)'
                                },
                                '&.Mui-disabled': {
                                  bgcolor: 'rgba(0, 0, 0, 0.12)',
                                  color: 'rgba(0, 0, 0, 0.26)'
                                }
                              }}
                              onClick={handleDisputeFormSubmit}
                              disabled={!disputeFormComplete || submittedForms['dispute']}
                            >
                              {submittedForms['dispute'] ? 'Form Submitted' : 'Submit Dispute'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    
                    {/* Block Card Form - Simplified */}
                    {activeFormType === 'Block Card' && (
                      <Grid container spacing={2}>
                        {/* Dispute Summary at the top */}
                        <Grid item xs={12}>
                          <Paper elevation={1} sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                            borderLeft: `4px solid ${theme.colors.primary}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Dispute Summary
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AutoAwesomeIcon />}
                                onClick={handleGenerateDisputeSummary}
                                disabled={isGeneratingSummary}
                                sx={{ 
                                  bgcolor: theme.colors.primary, 
                                  color: mode === 'dark' ? '#ffffff' : theme.colors.secondary,
                                  '&:hover': {
                                    bgcolor: theme.colors.primary,
                                    filter: 'brightness(0.9)'
                                  }
                                }}
                              >
                                {isGeneratingSummary ? `Generating... ${summaryTimer}s` : 'Generate Summary'}
                              </Button>
                            </Box>
                            {isTranscribing ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Click "Generate Summary" to create a summary from the current transcript.
                              </Typography>
                            ) : callSummary ? (
                              <Typography variant="body2">
                                {callSummary}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                No summary available. Click "Generate Summary" button to create one.
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        {/* Basic Customer Fields */}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Customer Name"
                            value={formData.customerName || disputeFormData.customerName}
                            onChange={(e) => handleFormChange('customerName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={formData.accountNumber || disputeFormData.accountNumber}
                            onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.customerPhone || disputeFormData.customerPhone}
                            onChange={(e) => handleFormChange('customerPhone', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="h6" sx={{ mt: 1, mb: 2 }}>
                            Card Block Request
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            This will initiate the process to block the customer's existing card due to suspected fraud or unauthorized activity.
                          </Typography>
                          <Box sx={{ bgcolor: 'rgba(0,0,0,0.04)', p: 3, borderRadius: 1, mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              Confirmation of actions to be taken:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <BlockIcon sx={{ mr: 1, color: '#f44336', fontSize: '1rem' }} />
                              <Typography variant="body2">Block existing card immediately</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <CheckCircleIcon sx={{ mr: 1, color: '#4caf50', fontSize: '1rem' }} />
                              <Typography variant="body2">Flag account for fraud monitoring</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CheckCircleIcon sx={{ mr: 1, color: '#4caf50', fontSize: '1rem' }} />
                              <Typography variant="body2">Send notification to customer</Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button 
                              variant="contained" 
                              color="primary"
                              startIcon={submittedForms['block'] ? <CheckCircleIcon /> : <BlockIcon />}
                              sx={{ 
                                bgcolor: submittedForms['block'] ? '#4caf50' : '#f44336', 
                                color: '#ffffff',
                                '&:hover': {
                                  bgcolor: submittedForms['block'] ? '#45a049' : '#d32f2f'
                                },
                                '&.Mui-disabled': {
                                  bgcolor: submittedForms['block'] ? '#4caf50' : 'rgba(0, 0, 0, 0.12)',
                                  color: submittedForms['block'] ? '#ffffff' : 'rgba(0, 0, 0, 0.26)'
                                }
                              }}
                              onClick={() => {
                                if (socket && sessionId) {
                                  socket.emit('submit_form', {
                                    session_id: sessionId,
                                    form_type: 'Block Card',
                                    form_data: { 
                                      customerName: formData.customerName || disputeFormData.customerName,
                                      customerAccount: formData.accountNumber || disputeFormData.accountNumber,
                                      customerPhone: formData.customerPhone || disputeFormData.customerPhone,
                                      disputeSummary: callSummary,
                                      actionTaken: "Card blocked due to suspected fraud or unauthorized activity"
                                    }
                                  });
                                  
                                  setSubmittedForms(prev => ({
                                    ...prev,
                                    'block': true
                                  }));
                                  
                                  // Update action status to completed
                                  if (selectedAction) {
                                    setClickedActions(prev => ({
                                      ...prev,
                                      [selectedAction]: 'completed'
                                    }));
                                  }
                                  
                                  // Update workflow state
                                  setWorkflowState("replace");
                                  
                                  setFormSubmitted(true);
                                  setShowSubmissionConfirmation(true);
                                  setFormSubmissionSuccess(true);
                                  
                                  setTimeout(() => {
                                    setFormSubmitted(false);
                                    setShowSubmissionConfirmation(false);
                                    setFormSubmissionSuccess(null);
                                    setFormVisible(false);
                                    setSelectedAction(null);
                                    setShowSubActions(false);
                                  }, 3000);
                                }
                              }}
                              disabled={submittedForms['block']}
                            >
                              {submittedForms['block'] ? 'Card Blocked' : 'Block Card Now'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    
                    {/* Card Request Form */}
                    {activeFormType === 'Card Request Form' && (
                      <Grid container spacing={2}>
                        {/* Dispute Summary at the top */}
                        <Grid item xs={12}>
                          <Paper elevation={1} sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                            borderLeft: `4px solid ${theme.colors.primary}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Dispute Summary
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AutoAwesomeIcon />}
                                onClick={handleGenerateDisputeSummary}
                                disabled={isGeneratingSummary}
                                sx={{ 
                                  bgcolor: theme.colors.primary, 
                                  color: mode === 'dark' ? '#ffffff' : theme.colors.secondary,
                                  '&:hover': {
                                    bgcolor: theme.colors.primary,
                                    filter: 'brightness(0.9)'
                                  }
                                }}
                              >
                                {isGeneratingSummary ? `Generating... ${summaryTimer}s` : 'Generate Summary'}
                              </Button>
                            </Box>
                            {isTranscribing ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Click "Generate Summary" to create a summary from the current transcript.
                              </Typography>
                            ) : callSummary ? (
                              <Typography variant="body2">
                                {callSummary}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                No summary available. Click "Generate Summary" button to create one.
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Customer Name"
                            value={cardRequestFormData.customerName}
                            onChange={(e) => handleCardRequestFormChange('customerName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={cardRequestFormData.customerPhone}
                            onChange={(e) => handleCardRequestFormChange('customerPhone', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={cardRequestFormData.customerEmail}
                            onChange={(e) => handleCardRequestFormChange('customerEmail', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={cardRequestFormData.customerAddress}
                            onChange={(e) => handleCardRequestFormChange('customerAddress', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date of Birth"
                            value={cardRequestFormData.dateOfBirth}
                            onChange={(e) => handleCardRequestFormChange('dateOfBirth', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last 4 of SSN"
                            value={cardRequestFormData.lastFourSSN}
                            onChange={(e) => handleCardRequestFormChange('lastFourSSN', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={cardRequestFormData.accountNumber}
                            onChange={(e) => handleCardRequestFormChange('accountNumber', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            label="Reason for Replacement"
                            value={cardRequestFormData.reason}
                            onChange={(e) => handleCardRequestFormChange('reason', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          >
                            {['Lost Card', 'Stolen Card', 'Damaged Card', 'Compromised Card', 'Name Change', 'Upgrade Card'].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Typography sx={{ mr: 2, fontWeight: 500 }}>Expedite Delivery?</Typography>
                            <Button 
                              variant={cardRequestFormData.expediteDelivery ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleCardRequestFormChange('expediteDelivery', true)}
                              sx={{ 
                                mr: 1,
                                bgcolor: cardRequestFormData.expediteDelivery ? theme.colors.primary : 'transparent',
                                color: cardRequestFormData.expediteDelivery ? theme.colors.secondary : theme.colors.primary,
                                borderColor: theme.colors.primary,
                                '&:hover': {
                                  bgcolor: cardRequestFormData.expediteDelivery ? theme.colors.primary : `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)`,
                                  filter: cardRequestFormData.expediteDelivery ? 'brightness(0.9)' : 'none'
                                }
                              }}
                            >
                              Yes
                            </Button>
                            <Button 
                              variant={!cardRequestFormData.expediteDelivery ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleCardRequestFormChange('expediteDelivery', false)}
                              sx={{
                                bgcolor: !cardRequestFormData.expediteDelivery ? theme.colors.primary : 'transparent',
                                color: !cardRequestFormData.expediteDelivery ? theme.colors.secondary : theme.colors.primary,
                                borderColor: theme.colors.primary,
                                '&:hover': {
                                  bgcolor: !cardRequestFormData.expediteDelivery ? theme.colors.primary : `rgba(${parseInt(theme.colors.primary.substring(1,3), 16)}, ${parseInt(theme.colors.primary.substring(3,5), 16)}, ${parseInt(theme.colors.primary.substring(5,7), 16)}, 0.1)`,
                                  filter: !cardRequestFormData.expediteDelivery ? 'brightness(0.9)' : 'none'
                                }
                              }}
                            >
                              No
                            </Button>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Delivery Address (if different)"
                            value={cardRequestFormData.deliveryAddress}
                            onChange={(e) => handleCardRequestFormChange('deliveryAddress', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Additional Notes"
                            value={cardRequestFormData.additionalNotes}
                            onChange={(e) => handleCardRequestFormChange('additionalNotes', e.target.value)}
                            variant="outlined"
                            multiline
                            rows={3}
                            sx={{ 
                              mb: 2,
                              '& .MuiInputBase-input': {
                                color: isGeneratingSummary ? theme.colors.primary : 'inherit'
                              }
                            }}
                            helperText={isGeneratingSummary ? `AI generating summary... ${summaryTimer}s elapsed` : ''}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button 
                              variant="contained" 
                              startIcon={<SendIcon />}
                              sx={{ 
                                bgcolor: theme.colors.primary, 
                                color: theme.colors.secondary,
                                '&:hover': {
                                  bgcolor: theme.colors.primary,
                                  filter: 'brightness(0.9)'
                                },
                                '&.Mui-disabled': {
                                  bgcolor: 'rgba(0, 0, 0, 0.12)',
                                  color: 'rgba(0, 0, 0, 0.26)'
                                }
                              }}
                              onClick={handleCardRequestFormSubmit}
                              disabled={!cardFormComplete || submittedForms['card']}
                            >
                              {submittedForms['card'] ? 'Form Submitted' : 'Submit Card Request'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                    
                    {/* General Form */}
                    {activeFormType === 'General Form' && (
                      <Grid container spacing={2}>
                        {/* Add Dispute Summary at the top */}
                        <Grid item xs={12}>
                          <Paper elevation={1} sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: 'rgba(0, 0, 0, 0.03)', 
                            borderLeft: `4px solid ${theme.colors.primary}`
                          }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Dispute Summary
                              </Typography>
                              <Button 
                                variant="contained" 
                                size="small" 
                                startIcon={<AutoAwesomeIcon />}
                                onClick={handleGenerateDisputeSummary}
                                disabled={isGeneratingSummary}
                                sx={{ 
                                  bgcolor: theme.colors.primary, 
                                  color: mode === 'dark' ? '#ffffff' : theme.colors.secondary,
                                  '&:hover': {
                                    bgcolor: theme.colors.primary,
                                    filter: 'brightness(0.9)'
                                  }
                                }}
                              >
                                {isGeneratingSummary ? `Generating... ${summaryTimer}s` : 'Generate Summary'}
                              </Button>
                            </Box>
                            {isTranscribing ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Click "Generate Summary" to create a summary from the current transcript.
                              </Typography>
                            ) : callSummary ? (
                              <Typography variant="body2">
                                {callSummary}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                No summary available. Click "Generate Summary" button to create one.
                              </Typography>
                            )}
                          </Paper>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Customer Name"
                            value={formData.customerName}
                            onChange={(e) => handleFormChange('customerName', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.customerPhone}
                            onChange={(e) => handleFormChange('customerPhone', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Email"
                            value={formData.customerEmail}
                            onChange={(e) => handleFormChange('customerEmail', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Address"
                            value={formData.customerAddress}
                            onChange={(e) => handleFormChange('customerAddress', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Date of Birth"
                            value={formData.dateOfBirth}
                            onChange={(e) => handleFormChange('dateOfBirth', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Last 4 of SSN"
                            value={formData.lastFourSSN}
                            onChange={(e) => handleFormChange('lastFourSSN', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Account Number"
                            value={formData.accountNumber}
                            onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Issue Description"
                            value={formData.issueDescription}
                            onChange={(e) => handleFormChange('issueDescription', e.target.value)}
                            variant="outlined"
                            multiline
                            rows={2}
                            sx={{ mb: 2 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            fullWidth
                            label="Request Type"
                            value={formData.requestType}
                            onChange={(e) => handleFormChange('requestType', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          >
                            {['Technical Support', 'Billing Question', 'Account Update', 'Complaint', 'Information Request'].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            select
                            fullWidth
                            label="Priority"
                            value={formData.priority}
                            onChange={(e) => handleFormChange('priority', e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mb: 2 }}
                          >
                            {['Low', 'Medium', 'High', 'Critical'].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Additional Notes"
                            value={formData.additionalNotes}
                            onChange={(e) => handleFormChange('additionalNotes', e.target.value)}
                            variant="outlined"
                            multiline
                            rows={3}
                            sx={{ 
                              mb: 2,
                              '& .MuiInputBase-input': {
                                color: isGeneratingSummary ? theme.colors.primary : 'inherit'
                              }
                            }}
                            helperText={isGeneratingSummary ? `AI generating summary... ${summaryTimer}s elapsed` : ''}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button 
                              variant="contained" 
                              startIcon={<SendIcon />}
                              sx={{ 
                                bgcolor: theme.colors.primary, 
                                color: theme.colors.secondary,
                                '&:hover': {
                                  bgcolor: theme.colors.primary,
                                  filter: 'brightness(0.9)'
                                }
                              }}
                              onClick={handleFormSubmit}
                              disabled={!formData.customerName || !formData.issueDescription}
                            >
                              Submit Form
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </Box>
              </>
            ) : (
              // Empty state with consistent styling
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                p: 3,
                color: textColor,
                textAlign: 'center'
              }}>
                <AssignmentIcon sx={{ fontSize: 60, color: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: textColor }}>
                  No Form Selected
                </Typography>
                <Typography variant="body2" sx={{ color: textColor }}>
                  Click on a recommended agent action to generate a form
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

export default LiveDiarization; 